import { useEffect, useMemo, useState } from "react";
import { Eye, FileBarChart2, FileUser, Loader2, Presentation } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, fetchReportJob, saveBlob } from "@/lib/api";
import { PptPreviewDialog } from "@/components/PptPreviewDialog";
import { BULAN } from "@/lib/format";
import { useFilters } from "@/context/FilterContext";
import { PageHeader } from "@/components/Shared";
import { TemplateCard } from "@/components/TemplateCard";
import { useAuth } from "@/context/AuthContext";

const ALL = "__all";

function Pick({ testid, label, value, onChange, options, allLabel }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? null : v)}>
        <SelectTrigger data-testid={testid} className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          {allLabel && <SelectItem value={ALL}>{allLabel}</SelectItem>}
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ReportCard({ icon: Icon, title, desc, items, children, onRun, busy, testid }) {
  return (
    <div className="flex flex-col rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#0078C1]/10 text-[#0078C1]"><Icon className="h-5 w-5" /></span>
        <div>
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{desc}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">{children}</div>
      <ul className="mt-4 flex-1 space-y-1 text-xs text-muted-foreground">
        {items.map((i) => <li key={i} className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#0078C1]" />{i}</li>)}
      </ul>
      <div className="mt-5 grid grid-cols-2 gap-2">
        <Button data-testid={`${testid}-preview`} variant="outline" onClick={() => onRun(true)} disabled={!!busy}>
          {busy === "preview" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />}Pratinjau
        </Button>
        <Button data-testid={testid} onClick={() => onRun(false)} disabled={!!busy}>
          {busy === "download" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Presentation className="mr-1.5 h-4 w-4" />}
          {busy === "download" ? "Menyusun PPT…" : "Unduh PPT"}
        </Button>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const { meta } = useFilters();
  const { user } = useAuth();
  const [tahun, setTahun] = useState(null);
  const [bulan, setBulan] = useState(null);
  const [uid, setUid] = useState(null);
  const [tipe, setTipe] = useState(null);
  const [jenis, setJenis] = useState("bulan");
  const [rjenis, setRjenis] = useState("bulan");
  const [unit, setUnit] = useState(null);
  const [ruid, setRuid] = useState(null);
  const [rtipe, setRtipe] = useState(null);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (meta?.latest && !tahun) { setTahun(String(meta.latest.tahun)); setBulan(String(meta.latest.bulan_no)); }
  }, [meta, tahun]);
  const units = useMemo(() => [...new Set((meta?.units || []).filter((u) => (!ruid || u.uid === ruid) && (!rtipe || u.tipe === rtipe)).map((u) => u.unit))]
    .map((u) => ({ value: u, label: u })), [meta, ruid, rtipe]);
  useEffect(() => {
    if (unit && !units.some((u) => u.value === unit)) setUnit(null);
  }, [units, unit]);
  const uidOpts = (meta?.uids || []).map((u) => ({ value: u, label: u }));
  const tipeOpts = (meta?.tipes || []).map((u) => ({ value: u, label: u }));
  const years = (meta?.years || []).map((y) => ({ value: String(y), label: String(y) }));
  const months = BULAN.map((b, i) => ({ value: String(i + 1), label: b }));

  const run = async (kind, params, name, asPreview) => {
    setBusy({ kind, step: asPreview ? "preview" : "download" });
    try {
      const p = { tahun, bulan, ...params };
      Object.keys(p).forEach((k) => p[k] == null && delete p[k]);
      const file = await fetchReportJob(kind, p, name);
      if (asPreview) setPreview(file);
      else { saveBlob(file.blob, file.name); toast.success("PPT berhasil diunduh"); }
    } catch (e) {
      let msg = errMsg(e);
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail; } catch (_) { /* keep */ } }
      toast.error(`Gagal menyusun laporan: ${msg}`);
    } finally { setBusy(null); }
  };

  const period = (
    <>
      <Pick testid="report-tahun-select" label="Tahun" value={tahun} onChange={setTahun} options={years} />
      <Pick testid="report-bulan-select" label="Bulan" value={bulan} onChange={setBulan} options={months} />
    </>
  );

  const jenisOpts = [{ value: "bulan", label: "Bulanan (bulan terpilih)" }, { value: "ytd", label: "Akumulasi (Januari s.d. bulan terpilih)" }];
  const stepOf = (kind) => (busy?.kind === kind ? busy.step : null);

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader eyebrow="Laporan" title="Laporan & Rapor (PPT)" desc="Disusun otomatis memakai template presentasi PLN Electricity Services, dari data snapshot terakhir. Periode berlaku untuk kedua laporan." />
      <div className="grid gap-6 lg:grid-cols-2">
        <ReportCard testid="download-monthly-ppt" icon={FileBarChart2} title="Laporan Kinerja (Bulanan / Akumulasi)" busy={stepOf("monthly")}
          desc="Deck gaya konsultan: kartu KPI, grafik, insight & rekomendasi; tabel rinci di lampiran."
          items={["Ringkasan eksekutif + kesimpulan otomatis sesuai filter", "Dashboard 11 indikator dengan perubahan vs tahun lalu", "Grafik tren: capaian, Cash In vs Target, RPT/RCT (All & Non CT), Denda vs Reward", "Peringkat unit + slide pembahasan tiap indikator (kartu + 2 grafik + insight)", "Kesimpulan & rekomendasi tindak lanjut, lampiran tabel per unit"]}
          onRun={(pv) => run("monthly", { uid, tipe, mode: jenis }, "Laporan_Kinerja.pptx", pv)}>
          <div className="col-span-2"><Pick testid="report-jenis-select" label="Jenis Laporan" value={jenis} onChange={(v) => setJenis(v || "bulan")}
            options={jenisOpts} />
            <p data-testid="report-jenis-note" className="mt-1 text-[11px] text-muted-foreground">{jenis === "ytd" ? "Akumulasi: angka dijumlah/dirata-rata Januari s.d. bulan terpilih." : "Bulanan: fokus angka di bulan terpilih saja (bukan akumulasi)."} Grafik tren ditampilkan Januari s.d. bulan terpilih.</p></div>
          {period}
          <Pick testid="report-uid-select" label="UID" value={uid} onChange={setUid} options={uidOpts} allLabel="Semua UID" />
          <Pick testid="report-tipe-select" label="Tipe Project" value={tipe} onChange={setTipe} options={tipeOpts} allLabel="Semua Tipe" />
        </ReportCard>
        <ReportCard testid="download-rapor-ppt" icon={FileUser} title="Rapor per Unit / UP3" busy={stepOf("rapor")}
          desc="Rapor ringkas 9 slide per unit: dashboard eksekutif, scorecard, dashboard KPI, tren, kualitas layanan, petugas & denda, data pengusahaan, kesimpulan."
          items={["Rapor Kinerja: kartu scorecard realisasi vs target, capaian bulanan/akumulasi & peringkat", "Dashboard 11 indikator utama vs tahun lalu", "Tren bulanan Pendapatan & SLA, BPP & RPT/RCT dengan Insight Eksekutif", "Kualitas layanan: Rating Negatif & Laporan Berulang (Marking/Non Marking) + feedback teratas", "Top & Bottom 5 petugas YO + kategori denda terbesar", "Data pengusahaan: market share kontrak + semua kolom aset (unit di-highlight)", "Kesimpulan & saran tindak lanjut (maks. 5)", "Filter UID & Tipe Project; semua unit = peringkat + rapor tiap unit", "Pratinjau di browser sebelum diunduh"]}
          onRun={(pv) => run("rapor", { unit, uid: ruid, tipe: rtipe, mode: rjenis }, "Rapor_Unit.pptx", pv)}>
          <div className="col-span-2"><Pick testid="report-rapor-jenis-select" label="Jenis Rapor" value={rjenis} onChange={(v) => setRjenis(v || "bulan")} options={jenisOpts} /></div>
          {period}
          <Pick testid="report-rapor-uid-select" label="UID" value={ruid} onChange={setRuid} options={uidOpts} allLabel="Semua UID" />
          <Pick testid="report-rapor-tipe-select" label="Tipe Project" value={rtipe} onChange={setRtipe} options={tipeOpts} allLabel="Semua Tipe" />
          <div className="col-span-2"><Pick testid="report-unit-select" label="Unit / UP3" value={unit} onChange={setUnit} options={units} allLabel={meta?.scoped ? "Semua unit saya (1 file)" : "Semua unit (1 file)"} /></div>
        </ReportCard>
      </div>
      {user?.role === "admin" && <TemplateCard />}
      <PptPreviewDialog file={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
